import { ApiRequestError } from "./client";

/** Fixed copy for statuses where the backend's own message either isn't
 * trustworthy (401 - see below) or the backend didn't supply one at all. */
const STATUS_FALLBACK: Record<number, string> = {
  403: "You don't have permission to perform this action.",
  404: "Requested resource not found.",
  409: "Duplicate record already exists.",
  429: "Too many requests. Please try again later.",
  500: "Something went wrong. Please try again.",
  502: "Server unavailable.",
  503: "Service temporarily unavailable.",
};

/**
 * Maps any error thrown by the API client to one human-readable string.
 * This is the single place that decides "what does the user see" for a
 * failed request - the global mutation-error handler uses it, and any
 * call site that still needs the raw string (e.g. to set a field error)
 * should call this instead of re-deriving its own fallback text.
 */
export function getErrorMessage(error: unknown): string {
  if (!(error instanceof ApiRequestError)) {
    return "Unexpected error occurred.";
  }

  const { appError, status } = error;

  // A 401 that actually reaches here has already survived the client's
  // silent refresh-and-retry - it's the fatal "your session is really
  // over" case, not a routine expired-access-token blip. The backend's
  // own message for that ("the access token has expired") isn't written
  // for a human, so this is a deliberate override, not a fallback.
  if (status === 401) {
    return "Session expired. Please log in again.";
  }

  if (appError.code === "TIMEOUT") {
    return "Request timed out. Please try again.";
  }
  if (appError.code === "NETWORK_OFFLINE") {
    return "No internet connection.";
  }
  // The request never reached the server at all (DNS failure, connection
  // refused, a CORS rejection) - distinct from NETWORK_OFFLINE (the browser
  // itself reports no connection) and from a normal HTTP error below (the
  // server responded, just unhappily).
  if (appError.code === "CONNECTION_FAILED") {
    return "Unable to reach the server. Check your connection and try again.";
  }
  // A transient failure while silently refreshing an expired access token -
  // NOT a rejected/expired refresh credential (that case is still a real
  // "Session expired", handled via the 401 branch above). The session is
  // still intact; only this one request needs retrying.
  if (appError.code === "SESSION_REFRESH_FAILED") {
    return "Couldn't verify your session. Check your connection and try again.";
  }

  // A validation error's top-level message is a generic "one or more
  // fields are invalid" - the actual useful text lives per-field. Joining
  // those is what makes the toast say "a monthly employee needs a basic
  // salary" instead of the generic wrapper message.
  if (appError.fields?.length) {
    return appError.fields.map((f) => f.message).join("; ");
  }

  if (appError.message) {
    return appError.message;
  }

  if (status && STATUS_FALLBACK[status]) {
    return STATUS_FALLBACK[status];
  }

  return "Unexpected error occurred.";
}
